

import {
    ExpandMore as ChevronDown,
    ChevronLeft,
    ChevronRight
} from '@mui/icons-material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import type { ArcFilters as IArcFilters } from '@/lib/arc-utils';
import type { Character, NarrativeStatus } from '@/lib/types';

interface ArcFiltersProps {
  open: boolean;
  onToggle: () => void;
  filters: IArcFilters;
  onFiltersChange: (filters: IArcFilters) => void;
  characters: Character[];
}

export function ArcFilters({
  open,
  onToggle,
  filters,
  onFiltersChange,
  characters,
}: ArcFiltersProps) {
  const { t } = useTranslation();

  const toggleType = (type: 'plot' | 'character') => {
    const newTypes = filters.type.includes(type)
      ? filters.type.filter((t) => t !== type)
      : [...filters.type, type];
    onFiltersChange({ ...filters, type: newTypes });
  };

  const toggleCharacter = (characterId: string) => {
    const newCharacterIds = filters.characterIds.includes(characterId)
      ? filters.characterIds.filter((id) => id !== characterId)
      : [...filters.characterIds, characterId];
    onFiltersChange({ ...filters, characterIds: newCharacterIds });
  };

  const toggleNarrativeStatus = (status: NarrativeStatus) => {
    const newStatus = filters.narrativeStatus.includes(status)
      ? filters.narrativeStatus.filter((s) => s !== status)
      : [...filters.narrativeStatus, status];
    onFiltersChange({ ...filters, narrativeStatus: newStatus });
  };

  const showCharacterFilter = filters.type.includes('character') || filters.type.length === 0;

  return (
    <Paper
      elevation={0}
      sx={{
        width: open ? 320 : 60,
        transition: 'width 0.3s ease-in-out',
        borderLeft: 1,
        borderColor: 'divider',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: open ? 'space-between' : 'center',
          minHeight: 64,
        }}
      >
        {open && (
          <Typography variant="h6" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
            {t('arcs.filters')}
          </Typography>
        )}
        <IconButton onClick={onToggle} size="small">
          {open ? <ChevronRight sx={{ fontSize: 20 }} /> : <ChevronLeft sx={{ fontSize: 20 }} />}
        </IconButton>
      </Box>

      {/* Filter Content */}
      {open && (
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          {/* Arc Type */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ChevronDown sx={{ fontSize: 20 }} />}>
              <Typography variant="subtitle2">{t('arcs.filter_arc_type')}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.type.includes('plot')}
                      onChange={() => toggleType('plot')}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">{t('arcs.filter_plot_arc')}</Typography>}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.type.includes('character')}
                      onChange={() => toggleType('character')}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">{t('arcs.filter_character_arc')}</Typography>}
                />
              </FormGroup>
            </AccordionDetails>
          </Accordion>

          {/* Characters (Conditional) */}
          {showCharacterFilter && (
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ChevronDown sx={{ fontSize: 20 }} />}>
                <Typography variant="subtitle2">{t('arcs.filter_characters')}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <FormGroup>
                  {characters.slice(0, 15).map((character) => (
                    <FormControlLabel
                      key={character.id}
                      control={
                        <Checkbox
                          checked={filters.characterIds.includes(character.id)}
                          onChange={() => toggleCharacter(character.id)}
                          size="small"
                        />
                      }
                      label={<Typography variant="body2">{character.name}</Typography>}
                    />
                  ))}
                </FormGroup>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Narrative Status */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ChevronDown sx={{ fontSize: 20 }} />}>
              <Typography variant="subtitle2">{t('arcs.filter_narrative_status')}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.narrativeStatus.includes('notWritten')}
                      onChange={() => toggleNarrativeStatus('notWritten')}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">{t('arcs.status_not_written')}</Typography>}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.narrativeStatus.includes('partial')}
                      onChange={() => toggleNarrativeStatus('partial')}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">{t('arcs.status_partial')}</Typography>}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filters.narrativeStatus.includes('complete')}
                      onChange={() => toggleNarrativeStatus('complete')}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">{t('arcs.status_complete')}</Typography>}
                />
              </FormGroup>
            </AccordionDetails>
          </Accordion>
        </Box>
      )}
    </Paper>
  );
}
